/**
 * contractVariableParser - Parser central de meta-variáveis para contratos
 *
 * Interpreta a sintaxe expandida de variáveis em templates de contrato:
 *   {{chave}}                     → Campo de texto simples
 *   {{*chave}}                    → Campo obrigatório
 *   {{chave||valor_padrão}}       → Campo com valor pré-preenchido
 *   {{chave[Op1, Op2]}}           → Dropdown (seleção única, separador vírgula)
 *   {{chave[Op1; Op2]}}           → Múltipla escolha (checkboxes, separador ponto-e-vírgula)
 *
 * Combinações suportadas: {{*combustivel||gasolina[Gasolina, Etanol, GNV]}}
 *
 * RELEASE: v1.0.0 - 2026-02-19
 */

import { PREDEFINED_VARIABLES } from '@/types/contracts';

/**
 * Representa uma variável parseada do HTML do template com todos os seus modificadores
 */
export interface ParsedVariable {
  /** Chave limpa da variável (sem *, sem modificadores) */
  key: string;
  /** Texto original dentro de {{ }} — usado para regex de substituição no HTML */
  rawKey: string;
  /** Label legível: usa PREDEFINED_VARIABLES ou humaniza a chave */
  label: string;
  /** Campo obrigatório (prefixo *) */
  required: boolean;
  /** Valor padrão pré-preenchido (modificador ||) */
  defaultValue?: string;
  /** Tipo de campo renderizado no wizard */
  type: 'text' | 'select' | 'multiselect';
  /** Opções disponíveis para select/multiselect */
  options?: string[];
}

/**
 * Humaniza uma chave de variável para exibição como label
 * Ex: "nome_cliente" → "Nome Cliente"
 */
function humanizeKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Parseia o rawKey extraído do HTML e retorna um ParsedVariable.
 * Exemplo de rawKey: "*combustivel||gasolina[Gasolina, Etanol, GNV]"
 */
function parseRawKey(rawKey: string): ParsedVariable {
  let rest = rawKey.trim();

  // Detecta obrigatório (prefixo *)
  const required = rest.startsWith('*');
  if (required) {
    rest = rest.slice(1);
  }

  // Extrai opções de lista [ ... ] — deve vir antes de extrair ||
  let options: string[] | undefined;
  let type: 'text' | 'select' | 'multiselect' = 'text';

  const bracketMatch = rest.match(/\[([^\]]+)\]$/);
  if (bracketMatch) {
    const optionsRaw = bracketMatch[1];
    // Remove o trecho [...] do restante para processar chave e defaultValue
    rest = rest.slice(0, rest.lastIndexOf('['));

    // Determina separador: ';' = múltipla escolha, ',' = seleção única
    if (optionsRaw.includes(';')) {
      type = 'multiselect';
      options = optionsRaw.split(';').map((o) => o.trim()).filter(Boolean);
    } else {
      type = 'select';
      options = optionsRaw.split(',').map((o) => o.trim()).filter(Boolean);
    }
  }

  // Extrai valor padrão (modificador ||)
  let defaultValue: string | undefined;
  const pipeIndex = rest.indexOf('||');
  if (pipeIndex !== -1) {
    defaultValue = rest.slice(pipeIndex + 2).trim() || undefined;
    rest = rest.slice(0, pipeIndex);
  }

  // Chave limpa final
  const key = rest.trim();

  // Resolve label: PREDEFINED_VARIABLES tem prioridade, senão humaniza a chave
  const predefined = PREDEFINED_VARIABLES.find((v) => v.key === key);
  const label = predefined?.label || humanizeKey(key);

  return { key, rawKey, label, required, defaultValue, type, options };
}

/**
 * Parseia todas as variáveis presentes no HTML de um template.
 * Ignora variáveis internas de loop (item_*) e marcadores de bloco (#produtos, /produtos).
 * Deduplicação por chave limpa — mantém primeira ocorrência.
 *
 * @param html - HTML do template de contrato
 * @returns Array de variáveis parseadas, sem duplicatas
 */
export function parseContractVariables(html: string): ParsedVariable[] {
  // Regex expandida: captura tudo dentro de {{ }} incluindo modificadores
  // Regex restritiva: aceita apenas caracteres válidos em definições de variável.
  // Rejeita tags HTML (<, >, ", =, :) para evitar capturar markup como rawKey.
  const regex = /\{\{([a-zA-Z0-9\u00C0-\u024F_\-*|[\],;\s./#]+)\}\}/g;
  const seen = new Set<string>();
  const result: ParsedVariable[] = [];

  // Chaves internas do loop de produtos — não devem virar campos do formulário
  // @release v1.2.0 — adicionadas chaves de serviço (avulso/recorrente)
  const loopInternalKeys = new Set([
    'item_nome', 'item_sku', 'item_tipo', 'item_unidade',
    'item_categoria', 'item_subcategoria',
    'item_quantidade', 'item_valor_unitario',
    'item_mensalidade', 'item_periodicidade',
    'item_prazo', 'item_prazo_quantidade', 'item_prazo_unidade',
    'item_desconto', 'item_desconto_percentual', 'item_desconto_fixo',
    'item_total', 'item_total_vigencia',
  ]);

  let match: RegExpExecArray | null;
  while ((match = regex.exec(html)) !== null) {
    const rawKey = match[1].trim();

    // Ignora marcadores de bloco
    if (rawKey.startsWith('#') || rawKey.startsWith('/')) continue;

    const parsed = parseRawKey(rawKey);

    // Ignora variáveis internas do loop
    if (loopInternalKeys.has(parsed.key)) continue;

    // Deduplicação por chave limpa
    if (seen.has(parsed.key)) continue;
    seen.add(parsed.key);

    result.push(parsed);
  }

  return result;
}

/**
 * Substitui todas as variáveis no HTML usando as rawKeys como padrão de busca.
 * Garante que {{*chave||padrão[...]}} seja substituído pelo valor informado.
 *
 * @param html     - HTML original do template
 * @param variables - Mapa chave→valor preenchido pelo usuário
 * @returns HTML com variáveis substituídas
 */
export function substituteVariables(html: string, variables: Record<string, string>): string {
  const parsed = parseContractVariables(html);

  for (const v of parsed) {
    // Escapa caracteres especiais do rawKey para uso em regex
    const escapedRaw = v.rawKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\{\\{${escapedRaw}\\}\\}`, 'g');
    const value = variables[v.key] ?? v.defaultValue ?? '';
    html = html.replace(regex, value || `<span style="color:red">{{${v.key}}}</span>`);
  }

  return html;
}
